import { Download, History, Layers, Pencil, RefreshCw, Search, Ticket, X } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { Loading } from '../components/Loading';
import { Pagination } from '../components/Pagination';
import { StatusBadge } from '../components/StatusBadge';
import { api, dateTime } from '../lib/api';
import type {
  PagaquiAvailableProduct,
  RefreshVoucherStockResult,
  VoucherCategoryWithCount,
  VoucherProduct,
  VoucherPurchase,
  VoucherPurchasePage,
} from '../lib/types';

type Tab = 'current' | 'available' | 'categories' | 'history';

const PURCHASES_PAGE_SIZE = 20;
const money = (value: number) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value);
function EmptyState({ title, description }: { title: string; description: string }) {
  return <div className="voucher-empty"><span><Ticket size={26} /></span><h3>{title}</h3><p>{description}</p></div>;
}
function SearchField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <label className="voucher-search"><Search size={18} /><input aria-label="Pesquisar vouchers" placeholder="Pesquisar por nome ou GTIN…" value={value} onChange={e => onChange(e.target.value)} /></label>;
}


export function PagaquiVouchersPage() {
  const [tab, setTab] = useState<Tab>('current');

  return (
    <div className="page vouchers-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Pagaqui</span>
          <h1>Vouchers</h1>
          <p>Catálogo de vouchers vendidos na app, importado seletivamente da Pagaqui.</p>
        </div>
      </div>

      <div className="account-tabs voucher-tabs">
        <button
          className={tab === 'current' ? 'account-tab account-tab--active' : 'account-tab'}
          onClick={() => setTab('current')}
        >
          <Ticket size={17} /> Vouchers atuais
        </button>
        <button
          className={tab === 'available' ? 'account-tab account-tab--active' : 'account-tab'}
          onClick={() => setTab('available')}
        >
          <Download size={17} /> Disponíveis para importar
        </button>
        <button
          className={tab === 'categories' ? 'account-tab account-tab--active' : 'account-tab'}
          onClick={() => setTab('categories')}
        >
          <Layers size={17} /> Categorias
        </button>
        <button
          className={tab === 'history' ? 'account-tab account-tab--active' : 'account-tab'}
          onClick={() => setTab('history')}
        >
          <History size={17} /> Histórico de compras
        </button>
      </div>

      {tab === 'current' && <CurrentVouchersTab />}
      {tab === 'available' && <AvailableVouchersTab />}
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'history' && <PurchaseHistoryTab />}
    </div>
  );
}

function CurrentVouchersTab() {
  const [items, setItems] = useState<VoucherProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [editTarget, setEditTarget] = useState<VoucherProduct | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshSummary, setRefreshSummary] = useState<RefreshVoucherStockResult | null>(null);

  const load = () =>
    api<{ items: VoucherProduct[] }>('/vouchers')
      .then((data) => setItems(data.items))
      .catch(() => setError('Não foi possível carregar os vouchers.'))
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  async function refreshStock() {
    setError('');
    setRefreshing(true);
    setRefreshSummary(null);
    try {
      const result = await api<RefreshVoucherStockResult>('/vouchers/refresh-stock', {
        method: 'POST',
      });
      setRefreshSummary(result);
      await load();
    } catch {
      setError('Não foi possível atualizar o stock. Tenta novamente.');
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel__heading">
        <div><h2>O teu catálogo</h2><p>Gere os vouchers publicados na app e acompanha o stock.</p></div>
        <button className="button button--secondary" onClick={refreshStock} disabled={refreshing}>
          <RefreshCw size={16} /> {refreshing ? 'A atualizar…' : 'Atualizar stock'}
        </button>
      </div>
      <div className="voucher-toolbar"><SearchField value={query} onChange={setQuery} /><span>{items.length} vouchers no catálogo</span></div>
      {error && <div className="form-error" role="alert">{error}</div>}
      {refreshSummary && (
        <p className="form-success">
          {refreshSummary.updated} atualizados, {refreshSummary.soldOut} esgotados, de{' '}
          {refreshSummary.totalOptionsFromPagaqui} opções devolvidas pela Pagaqui.
        </p>
      )}
      {loading ? (
        <Loading />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Categoria</th>
                <th>GTIN / opções</th>
                <th>Stock</th>
                <th>Criado</th>
                <th>Atualizado</th>
                <th>Estado</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {items.filter(p => `${p.name} ${p.options.map(o => o.productOptionCode).join(' ')}`.toLowerCase().includes(query.toLowerCase())).map((product) => (
                <tr key={product.id}>
                  <td><strong>{product.name}</strong></td>
                  <td>{product.category?.name ?? '—'}</td>
                  <td>
                    {product.options.map((o) => (
                      <div key={o.id}>
                        <code>{o.productOptionCode}</code> — {money(o.value)}
                      </div>
                    ))}
                  </td>
                  <td>
                    {product.options.map((o) => (
                      <div key={o.id}><span className={`voucher-stock ${o.stock === 0 ? 'voucher-stock--empty' : ''}`}>{o.stock === null ? 'Por consultar' : o.stock === 0 ? 'Esgotado' : `${o.stock} disponíveis`}</span></div>
                    ))}
                  </td>
                  <td>{dateTime(product.createdAt)}</td>
                  <td>{dateTime(product.updatedAt)}</td>
                  <td><StatusBadge value={product.isActive ? 'ACTIVE' : 'SUSPENDED'} /></td>
                  <td>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => setEditTarget(product)}
                      aria-label="Editar voucher"
                      title="Editar"
                    >
                      <Pencil size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {items.length > 0 && !items.some(p => `${p.name} ${p.options.map(o => o.productOptionCode).join(' ')}`.toLowerCase().includes(query.toLowerCase())) && <tr><td colSpan={8}><EmptyState title="Sem resultados" description="Experimenta outro nome ou GTIN." /></td></tr>}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8}><EmptyState title="O catálogo começa aqui" description="Abre Disponíveis para importar e escolhe os primeiros vouchers para a app." /></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {editTarget && (
        <EditVoucherModal
          voucher={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => {
            setEditTarget(null);
            await load();
          }}
        />
      )}
    </section>
  );
}

function EditVoucherModal({
  voucher,
  onClose,
  onSaved,
}: {
  voucher: VoucherProduct;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [description, setDescription] = useState(voucher.description ?? '');
  const [imageUrl, setImageUrl] = useState(voucher.imageUrl ?? '');
  const [categoryId, setCategoryId] = useState(voucher.category?.id ?? '');
  const [isActive, setIsActive] = useState(voucher.isActive);
  const [categories, setCategories] = useState<VoucherCategoryWithCount[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api<{ items: VoucherCategoryWithCount[] }>('/vouchers/categories').then((data) =>
      setCategories(data.items),
    ).catch(() => setError('Não foi possível carregar as categorias.'));
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api(`/vouchers/${voucher.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          description: description || null,
          imageUrl: imageUrl || null,
          categoryId: categoryId || null,
          isActive,
        }),
      });
      await onSaved();
    } catch {
      setError('Não foi possível guardar as alterações.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={submit}>
        <div className="modal__heading">
          <div><span className="eyebrow">Vouchers</span><h2>{voucher.name}</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar janela"><X size={20} /></button>
        </div>
        <label>
          Descrição
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </label>
        <label>
          Imagem (URL)
          <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…" />
        </label>
        <label>
          Categoria
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Sem categoria</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Ativo (visível na app)
        </label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal__actions">
          <button type="button" className="button button--secondary" onClick={onClose}>Cancelar</button>
          <button className="button button--primary" disabled={busy}>{busy ? 'A guardar…' : 'Guardar'}</button>
        </div>
      </form>
    </div>
  );
}

function AvailableVouchersTab() {
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<PagaquiAvailableProduct[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<string | null>(null);

  async function sync() {
    setLoading(true);
    setError('');

    try {
      const data = await api<{ items: PagaquiAvailableProduct[] }>(
        '/vouchers/pagaqui-available',
      );
      setProducts(data.items);
      setSelected(new Set());
    } catch {
      setError('Não foi possível obter o catálogo da Pagaqui. Confirma a configuração.');
    } finally {
      setLoading(false);
    }
  }

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function importSelected() {
    if (!products) return;
    setImporting(true);
    setError('');
    setImportSummary(null);
    try {
      const selections = products.flatMap((p) =>
        p.options
          .filter((o) => selected.has(o.productOptionCode))
          .map((o) => ({
            providerCode: p.providerCode,
            productCode: p.productCode,
            productName: p.name,
            productDescription: p.description,
            productOptionCode: o.productOptionCode,
            optionName: o.name,
            value: o.value,
          })),
      );
      const result = await api<{ imported: number; skippedExisting: number; stockUpdated: boolean }>(
        '/vouchers/import',
        { method: 'POST', body: JSON.stringify({ selections }) },
      );
      setImportSummary(`${result.imported} importados, ${result.skippedExisting} já existiam.`);
      await sync();
      if (!result.stockUpdated) setError('Os vouchers foram importados, mas não foi possível guardar o stock. Usa Atualizar stock no catálogo para tentar novamente.');
    } catch {
      setError('Não foi possível concluir a importação. Sincroniza o catálogo antes de tentar novamente.');
    } finally {
      setImporting(false);
    }
  }

  const availableOptions =
    products?.flatMap((p) =>
      p.options
        .filter((o) => !o.alreadyImported)
        .map((o) => ({ product: p, option: o })),
    ) ?? [];

  const visibleOptions = availableOptions.filter(({ product, option }) => `${product.name} ${option.name ?? ''} ${option.productOptionCode}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <section className="panel">
      <div className="panel__heading">
        <div><h2>Disponíveis para importar</h2><p>Escolhe as opções da Pagaqui que queres adicionar ao catálogo.</p></div>
        <button className="button button--secondary" onClick={sync} disabled={loading || importing}>
          <RefreshCw size={16} /> {loading ? 'A sincronizar…' : 'Sincronizar'}
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}
      {importSummary && <p className="form-success">{importSummary}</p>}
      {loading && <Loading />}
      {products && (
        <>
          <div className="voucher-toolbar"><SearchField value={query} onChange={setQuery} /><span>{availableOptions.length} opções disponíveis</span></div>
          <div className="voucher-import-bar"><span><strong>{selected.size}</strong> opções selecionadas</span><button className="button button--primary" onClick={importSelected} disabled={!selected.size || importing || loading}><Download size={16} />{importing ? 'A importar e guardar stock…' : 'Importar selecionados'}</button></div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th><input type="checkbox" aria-label="Selecionar todas as opções visíveis" disabled={importing || loading || !visibleOptions.length} checked={visibleOptions.length > 0 && visibleOptions.every(({ option }) => selected.has(option.productOptionCode))} onChange={e => setSelected(previous => { const next = new Set(previous); visibleOptions.forEach(({ option }) => e.target.checked ? next.add(option.productOptionCode) : next.delete(option.productOptionCode)); return next; })} /></th>
                  <th>Produto</th>
                  <th>Opção</th>
                  <th>GTIN</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {visibleOptions.map(({ product, option }) => (
                  <tr key={option.productOptionCode} className={selected.has(option.productOptionCode) ? 'voucher-row--selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Selecionar ${product.name} ${option.name ?? option.productOptionCode}`}
                        disabled={importing || loading}
                        checked={selected.has(option.productOptionCode)}
                        onChange={() => toggle(option.productOptionCode)}
                      />
                    </td>
                    <td><strong>{product.name}</strong></td>
                    <td>{option.name ?? '—'}</td>
                    <td><code>{option.productOptionCode}</code></td>
                    <td>{money(option.value)}</td>
                  </tr>
                ))}
                {visibleOptions.length === 0 && (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState title={availableOptions.length ? "Sem resultados" : "Catálogo em dia"} description={availableOptions.length ? "Experimenta outro nome ou GTIN." : "Todas as opções devolvidas já estão importadas."} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!products && !loading && !error && (
        <EmptyState title="Descobre os vouchers disponíveis" description="Sincroniza o catálogo da Pagaqui para escolher os vouchers. O stock é atualizado automaticamente ao importar." />
      )}
    </section>
  );
}

function CategoriesTab() {
  const [items, setItems] = useState<VoucherCategoryWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () =>
    api<{ items: VoucherCategoryWithCount[] }>('/vouchers/categories')
      .then((data) => setItems(data.items))
      .catch(() => setError('Não foi possível carregar as categorias.'))
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/vouchers/categories', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      setName('');
      await load();
    } catch {
      setError('Não foi possível criar a categoria (nome já existe?).');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setError('');
    try {
      await api(`/vouchers/categories/${id}`, { method: 'DELETE' });
      await load();
    } catch {
      setError('Não foi possível remover a categoria. Tenta novamente.');
    }
  }

  return (
    <section className="panel">
      <div className="panel__heading">
        <div><h2>Categorias</h2><p>Organiza o catálogo para facilitar a descoberta na app.</p></div>
      </div>
      <form className="inline-form" onSubmit={create}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Nome da categoria"
          placeholder="Ex.: Entretenimento"
          required
        />
        <button className="button button--primary" disabled={busy}>
          {busy ? 'A criar…' : 'Criar categoria'}
        </button>
      </form>
      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <Loading />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Nº de vouchers</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.productCount}</td>
                  <td>
                    <button
                      type="button"
                      className="table-action"
                      onClick={() => remove(c.id)}
                      disabled={c.productCount > 0}
                      title={
                        c.productCount > 0
                          ? 'Remove ou muda os vouchers desta categoria primeiro'
                          : 'Remover'
                      }
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={3}><EmptyState title="Organiza o teu catálogo" description="Cria a primeira categoria usando o campo acima." /></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PurchaseHistoryTab() {
  const [data, setData] = useState<VoucherPurchasePage | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responseTarget, setResponseTarget] = useState<VoucherPurchase | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(PURCHASES_PAGE_SIZE),
    });
    api<VoucherPurchasePage>(`/vouchers/purchases?${query}`)
      .then(setData)
      .catch(() => setError('Não foi possível carregar o histórico de compras.'))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <section className="panel">
      <div className="panel__heading">
        <div><h2>Histórico de compras</h2><p>Consulta as transações, os pagamentos e o estado de cada compra.</p></div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <Loading />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Voucher</th>
                <th>Quantidade</th>
                <th>Valor pago</th>
                <th>Estado</th>
                <th>Data</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {data?.items.map((purchase) => (
                <tr key={purchase.id}>
                  <td>
                    <strong>{purchase.customerName ?? '—'}</strong>
                    <div className="table-subtitle">{purchase.customerEmail ?? '—'}</div>
                  </td>
                  <td>
                    {purchase.voucherName ?? '—'}
                    {purchase.productOptionCode && (
                      <div className="table-subtitle"><code>{purchase.productOptionCode}</code></div>
                    )}
                  </td>
                  <td>{purchase.quantity}</td>
                  <td>{money(purchase.amount)}</td>
                  <td>
                    <StatusBadge value={purchase.status} />
                    {purchase.errorMessage && (
                      <div className="table-subtitle">{purchase.errorMessage}</div>
                    )}
                  </td>
                  <td>{dateTime(purchase.createdAt)}</td>
                  <td>
                    {purchase.responsePayload != null && (
                      <button
                        type="button"
                        className="table-action"
                        onClick={() => setResponseTarget(purchase)}
                      >
                        Ver resposta
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(data?.items.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={7}><EmptyState title="Ainda sem compras" description="As compras de vouchers vão aparecer aqui, com todos os detalhes." /></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <Pagination
          page={page}
          pageSize={PURCHASES_PAGE_SIZE}
          total={data.total}
          onPageChange={setPage}
        />
      )}
      {responseTarget && (
        <PurchaseResponseModal
          purchase={responseTarget}
          onClose={() => setResponseTarget(null)}
        />
      )}
    </section>
  );
}

function PurchaseResponseModal({
  purchase,
  onClose,
}: {
  purchase: VoucherPurchase;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal__heading">
          <div>
            <span className="eyebrow">Resposta Pagaqui</span>
            <h2>{purchase.voucherName ?? 'Voucher'}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar janela"><X size={20} /></button>
        </div>
        <pre className="json-viewer">
          {JSON.stringify(purchase.responsePayload, null, 2)}
        </pre>
        <div className="modal__actions">
          <button type="button" className="button button--secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
